// Financial helper utilities used by AR and Contracts

// Normalize frequency to canonical values
export const normalizeFrequency = (raw) => {
  const s = String(raw || '').toLowerCase().replace(/_/g, '-').trim();
  if (s.includes('month')) return 'monthly';
  if (s === 'one-time' || s === 'one time' || s === 'one') return 'one-time';
  return 'one-time';
};

export const isMonthlyFrequency = (f) => normalizeFrequency(f) === 'monthly';

// For a given service determine the vendor base used for AP
export const getServiceVendorBase = ({ internalUnitCost, unitPrice, quantity }) => {
  const internal = Number(internalUnitCost);
  const unit = Number(unitPrice);
  const qty = Number(quantity) || 1;
  const perUnit = (Number.isFinite(internal) && internal > 0)
    ? internal
    : ((Number.isFinite(unit) && unit > 0) ? unit : 0);
  return perUnit * qty;
};

// Determine whether a monthly service should be included for a given YYYY-MM
export const shouldIncludeServiceForMonth = ({ service, ym }) => {
  if (!isMonthlyFrequency(service?.frequency)) return false;
  const cancelYm = String(service?.cancellationEffectiveYm || '');
  return !cancelYm || String(ym) < cancelYm;
};

export const findFirstScheduledIndex = (milestoneLogs) => {
  const logs = Array.isArray(milestoneLogs) ? milestoneLogs : [];
  return logs.findIndex((m) => m && m.status === 'Scheduled');
};

// Build vendor components for a milestone
// oneTimeServiceIdsToPost: explicit list of one-time service IDs that should be posted for this milestone
export const buildVendorComponents = ({ services, ym, firstScheduledIdx, currentIdx, entities, oneTimeServiceIdsToPost = [] }) => {
  const list = Array.isArray(services) ? services : [];
  const vendorComponents = [];
  const debugRows = [];
  const explicitOneTimeIds = new Set(oneTimeServiceIdsToPost || []);
  
  list.forEach((svc) => {
    const base = getServiceVendorBase({
      internalUnitCost: svc.internalUnitCost,
      unitPrice: svc.unitPrice,
      quantity: svc.quantity,
    });
    const isMonthly = isMonthlyFrequency(svc.frequency);
    const isOneTime = !isMonthly;
    const serviceId = svc.id || svc.originalCostId || svc.original_cost_id || null;
    
    // For monthly: use standard YM filtering
    // For one-time: ONLY include if explicitly passed in oneTimeServiceIdsToPost
    const include = isMonthly
      ? shouldIncludeServiceForMonth({ service: svc, ym })
      : (serviceId && explicitOneTimeIds.has(serviceId));
    
    const rawVendorId = svc.vendorEntityId || svc.vendorId || svc.entity_id || (svc.vendor && (svc.vendor.id || svc.vendor.entity_id)) || null;
    debugRows.push({
      serviceName: svc?.name || svc?.serviceName || '',
      serviceId,
      frequency: svc?.frequency,
      ym,
      currentIdx,
      firstScheduledIdx,
      include,
      inExplicitList: serviceId ? explicitOneTimeIds.has(serviceId) : false,
      base,
      rawVendorId,
      oneTimeApplied: !!svc?.oneTimeApplied,
      oneTimeAppliedMilestoneId: svc?.oneTimeAppliedMilestoneId || null,
    });
    if (include && base > 0 && rawVendorId) {
      const ent = (Array.isArray(entities) ? entities : []).find((e) => e.id === rawVendorId);
      let directVendorId = rawVendorId;
      let subAttribution = null;
      if (ent && String(ent.accountType || '').toLowerCase() === 'sub' && ent.parentAccountId) {
        directVendorId = ent.parentAccountId;
        subAttribution = rawVendorId;
      }
      vendorComponents.push({
        vendorId: directVendorId,
        amountUsd: base,
        subAttribution,
        serviceId,
        serviceName: svc.name || svc.serviceName || ''
      });
    }
  });
  console.log('[buildVendorComponents] Result:', {
    ym,
    currentIdx,
    firstScheduledIdx,
    oneTimeServiceIdsToPost,
    servicesCount: list.length,
    vendorComponentsCount: vendorComponents.length,
    vendorComponents,
    debugRows,
  });
  return vendorComponents;
};

export const computeProcessingPercent = ({ method, financeConfig }) => {
  const ccPct = Math.max(0, Math.min(100, Number(financeConfig?.creditCardProcessingPercent || 0)));
  const checkPct = Math.max(0, Math.min(100, Number(financeConfig?.checkProcessingPercent || 0)));
  const m = String(method || '').toLowerCase();
  if (m === 'credit card' || m === 'creditcard' || m === 'credit_card') return ccPct;
  if (m === 'check') return checkPct;
  return 0;
};

export const computeTransportPercent = ({ method, financeConfig }) => {
  // Transport percent from financeConfig, can be method-specific or global
  const ccTransportPct = Math.max(0, Math.min(100, Number(financeConfig?.creditCardTransportPercent || financeConfig?.transportPercent || 0)));
  const checkTransportPct = Math.max(0, Math.min(100, Number(financeConfig?.checkTransportPercent || financeConfig?.transportPercent || 0)));
  const defaultPct = Math.max(0, Math.min(100, Number(financeConfig?.transportPercent || 0)));
  const m = String(method || '').toLowerCase();
  if (m === 'credit card' || m === 'creditcard' || m === 'credit_card') return ccTransportPct;
  if (m === 'check') return checkTransportPct;
  return defaultPct;
};


